import { portalClient } from "../api";

/**
 * Shared client for the public model directory
 * (GET /api/portal/v1/models/directory). Used by the public /models page
 * and by the landing page's live model preview — both render the same
 * data, so they share one fetch shape and one modality bucketing rule.
 */

/** Shape returned by GET /api/portal/v1/models/directory. */
export interface DirectoryItem {
  model_name: string;
  display_name: string;
  description: string;
  icon_key: string;
  tags: string[];
  vendor: string;
  vendor_icon: string;
  providers: string[];
  protocols: string[];
  capabilities: string[];
  /** Curated context window in tokens; absent = unknown. */
  context_tokens?: number;
  /** Lowest active per-million-token prices (microunits, 1e6 = $1). */
  input_price?: number | null;
  output_price?: number | null;
}

export type DirectoryState = "loading" | "ok" | "unauthorized" | "error";

export async function fetchDirectory(): Promise<{
  items: DirectoryItem[];
  state: DirectoryState;
}> {
  try {
    const data = await portalClient.get<{ items: DirectoryItem[] }>(
      "/api/portal/v1/models/directory",
      { authenticated: false },
    );
    return { items: data.items ?? [], state: "ok" };
  } catch (error) {
    const status = (error as { status?: number }).status;
    if (status === 401 || status === 403) return { items: [], state: "unauthorized" };
    return { items: [], state: "error" };
  }
}

/* The landing page reads the directory from three separate sections
   (hero stats, vendor marquee, model preview). One shared promise keeps
   that at a single network request per page load. */
let directoryCache: ReturnType<typeof fetchDirectory> | null = null;

export function fetchDirectoryCached() {
  if (!directoryCache) {
    directoryCache = fetchDirectory();
    // A failed load must not poison the cache for the next visit.
    directoryCache.then((r) => {
      if (r.state !== "ok") directoryCache = null;
    });
  }
  return directoryCache;
}

/** Capability keyword → modality bucket, used for the filter pills. */
export function modalitiesOf(capabilities: string[]): string[] {
  const mods = new Set<string>();
  for (const cap of capabilities) {
    const c = cap.toLowerCase();
    if (c.includes("image") || c.includes("vision")) mods.add("image");
    if (c.includes("audio") || c.includes("speech") || c.includes("transcri")) mods.add("audio");
    if (c.includes("video")) mods.add("video");
    if (c.includes("text") || c.includes("chat") || c.includes("generate-content") || c.includes("completion")) {
      mods.add("text");
    }
  }
  if (mods.size === 0) mods.add("text");
  return [...mods];
}
